<!--
  ~ Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
  ~ Venelin Iliev <http://veneliniliev.com>
  -->

<div class="container-fluid" *ngIf="accessService.hasAccess([], ['clients.menu'])">
  <div class="d-flex justify-content-between align-items-center">
    <div>
      <h5 class="title-secondary">{{ 'CLIENTS.CLIENTS_LIST.TITLE' | translate }}</h5>
    </div>

    <form class="d-flex" [formGroup]="form" (ngSubmit)="loadData()">
      <div class="align-self-end">
        <button type="button" class="btn btn-link btn-clear-filters" (click)="clearFilters()">
          {{ 'CLEAR_FILTERS' | translate }}
        </button>
      </div>

      <div class="custom-control custom-checkbox checkbox-margin me-3">
        <input type="checkbox" class="custom-control-input" formControlName="exact" id="client-list-exact" />
        <label class="custom-control-label" for="client-list-exact">
          {{ 'CLIENTS.CLIENTS_LIST.EXACT' | translate }}
        </label>
      </div>

      <div>
        <label>{{ 'CLIENTS.CLIENTS_LIST.KEYWORD' | translate }}</label>
        <input type="text" class="form-control" formControlName="keywords" />
      </div>

      <div class="d-flex">
        <button type="submit" class="btn btn-success btn-input align-self-end">
          {{ 'CLIENTS.CLIENTS_LIST.SEARCH' | translate }}
        </button>
      </div>

      <div class="ms-3 align-self-end">
        <button
          *ngIf="
            accessService.hasAccess(
              [
                UserRoleType.RESERVATION_MANAGER,
                UserRoleType.SALES_AGENT,
                UserRoleType.SALES_MANAGER,
                UserRoleType.CUSTOMER_CARE_AGENT,
                UserRoleType.CUSTOMER_CARE_MANAGER,
                UserRoleType.LEAD_GENERATION
              ],
              ['clients.store']
            )
          "
          type="button"
          class="btn btn-secondary btn-input"
          (click)="add()"
        >
          {{ 'CLIENTS.CLIENTS_LIST.ADD_CLIENT' | translate }}
        </button>
      </div>
    </form>
  </div>
  <kendo-grid
    [data]="data"
    [pageSize]="clientsResponse?.meta.per_page"
    [sortable]="true"
    [filterable]="true"
    [columnMenu]="true"
    [filter]="state?.filter"
    [sort]="state?.sort"
    [skip]="state?.skip"
    [pageable]="true"
    (dataStateChange)="loadData($event)"
    (columnVisibilityChange)="onVisibilityChange($event)"
    #grid
    (excelExport)="onExcelExport($event, 'Clients', grid)"
  >
    <kendo-grid-column
      [width]="70"
      class="text-center"
      headerClass="text-center"
      title="{{ 'CLIENTS.CLIENTS_LIST.NUMBER' | translate }}"
    >
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        {{ rowIndex + 1 }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="id"
      title="{{ 'CLIENTS.CLIENTS_LIST.ID' | translate }}"
      filter="numeric"
      [width]="150"
      [hidden]="isHiddenColumn('id', true)"
    ></kendo-grid-column>

    <kendo-grid-column
      field="name"
      title="{{ 'CLIENTS.CLIENTS_LIST.NAME' | translate }}"
      filter="text"
      [hidden]="isHiddenColumn('name')"
    >
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <app-client-box [client]="dataItem" [targetBlank]="true"></app-client-box>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="type.value"
      title="{{ 'CLIENTS.CLIENTS_LIST.TYPE' | translate }}"
      filter="text"
      [hidden]="isHiddenColumn('type.value')"
    >
      <ng-template kendoGridFilterCellTemplate let-filter>
        <app-kendo-type-select [groupType]="'clients'" [filter]="filter"></app-kendo-type-select>
      </ng-template>

      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <app-type-badge [type]="dataItem.type"></app-type-badge>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="country.name"
      title="{{ 'CLIENTS.CLIENTS_LIST.COUNTRY' | translate }}"
      filter="text"
      [hidden]="isHiddenColumn('country.name')"
    >
      <ng-template kendoGridFilterCellTemplate let-filter>
        <app-kendo-country-select [filter]="filter"></app-kendo-country-select>
      </ng-template>

      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <app-country-box [country]="dataItem.country"></app-country-box>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="city.name"
      title="{{ 'CLIENTS.CLIENTS_LIST.CITY' | translate }}"
      filter="text"
      [hidden]="isHiddenColumn('city.name', true)"
    >
      <ng-template kendoGridFilterCellTemplate let-filter>
        <app-kendo-city-select [filter]="filter"></app-kendo-city-select>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="contacts"
      title="{{ 'CLIENTS.CLIENTS_LIST.CONTACTS' | translate }}"
      [filterable]="false"
      [sortable]="false"
    >
      <ng-template kendoGridCellTemplate let-dataItem>
        <div *ngFor="let contact of dataItem.contacts; let i = index">
          <span class="contact-key">{{ contact?.type?.value }}:</span>
          <span class="contact-value">{{ contact?.value }}<span *ngIf="contact?.note"> ({{ contact?.note }})</span></span>
        </div>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      title="{{ 'CLIENTS.CLIENTS_LIST.ACTIONS' | translate }}"
      [width]="100"
      [class]="'text-center'"
      [includeInChooser]="false"
    >
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <app-preview-button
          *ngIf="
            accessService.hasAccess(
              [
                UserRoleType.RESERVATION_AGENT,
                UserRoleType.RESERVATION_MANAGER,
                UserRoleType.CONTRACTING_AGENT,
                UserRoleType.CONTRACTING_MANAGER,
                UserRoleType.SALES_AGENT,
                UserRoleType.SALES_MANAGER,
                UserRoleType.CUSTOMER_CARE_AGENT,
                UserRoleType.CUSTOMER_CARE_MANAGER,
                UserRoleType.FINANCE_AGENT,
                UserRoleType.FINANCE_MANAGER,
                UserRoleType.LEAD_GENERATION,
                UserRoleType.MARKETING,
                UserRoleType.ACCOUNTANT
              ],
              ['clients.show']
            )
          "
          [link]="['/clients/view']"
          [params]="{ id: dataItem.id }"
        >
        </app-preview-button>

        <app-edit-button
          *ngIf="
            accessService.hasAccess(
              [
                UserRoleType.RESERVATION_MANAGER,
                UserRoleType.CONTRACTING_MANAGER,
                UserRoleType.SALES_AGENT,
                UserRoleType.SALES_MANAGER,
                UserRoleType.CUSTOMER_CARE_AGENT,
                UserRoleType.CUSTOMER_CARE_MANAGER,
                UserRoleType.FINANCE_AGENT,
                UserRoleType.FINANCE_MANAGER,
                UserRoleType.LEAD_GENERATION
              ],
              ['clients.show']
            )
          "
          (click)="edit(dataItem)"
        >
        </app-edit-button>

        <app-delete-button
          *ngIf="
            accessService.hasAccess(
              [
                UserRoleType.RESERVATION_MANAGER,
                UserRoleType.SALES_AGENT,
                UserRoleType.SALES_MANAGER,
                UserRoleType.CUSTOMER_CARE_AGENT,
                UserRoleType.CUSTOMER_CARE_MANAGER,
                UserRoleType.LEAD_GENERATION
              ],
              ['clients.destroy']
            )
          "
          [swal]="{}"
          (confirm)="delete(dataItem)"
        >
        </app-delete-button>

        <app-phone-calls-finder-button [id]="dataItem.id" [type]="LaravelModelsEnum.CLIENT">
        </app-phone-calls-finder-button>
      </ng-template>
    </kendo-grid-column>

    <ng-template kendoPagerTemplate let-totalPages="totalPages" let-currentPage="currentPage">
      <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
      <kendo-pager-input></kendo-pager-input>
      <kendo-pager-info></kendo-pager-info>
      <kendo-pager-next-buttons></kendo-pager-next-buttons>
      <kendo-pager-page-sizes [pageSizes]="env.kendoGridPageSizes"></kendo-pager-page-sizes>
      <app-kendo-grid-refresh-button (click)="loadData(state)"></app-kendo-grid-refresh-button>
      <button type="button" class="btn kendo-export-btn" [disabled]="grid.loading" kendoGridExcelCommand>
        {{ 'EXPORT_TO_EXCEL' | translate }}
      </button>
    </ng-template>

    <kendo-grid-excel></kendo-grid-excel>
  </kendo-grid>
</div>
